import { api } from './api';

/**
 * Money on a schedule: installment plans on package orders (D-355) and
 * pledges (D-356). Kept beside api.ts, like api-payments.ts.
 */

export interface InstallmentTerms {
  installment_count: number;
  interval: 'MONTH';
  offered_on_web: boolean;
}

export interface InstallmentCharge {
  sequence: number;
  amount_cents: number;
  due_at: string;
  status: string;
  charged_cents: number | null;
  paid_at: string | null;
  attempt_count: number;
  next_attempt_at: string | null;
  last_failure_message: string | null;
}

export interface InstallmentPlan {
  id: string;
  status: string;
  installment_count: number;
  interval: string;
  total_cents: number;
  card_saved: boolean;
  consent_text: string;
  consented_at: string;
  stopped_at: string | null;
  stop_reason: string | null;
  defaulted_at: string | null;
  completed_at: string | null;
  charges: InstallmentCharge[];
  charged_cents: number;
  scheduled_cents: number;
}

export type PledgeInterval = 'MONTH' | 'QUARTER' | 'YEAR';
export type CollectionMethod = 'AUTOMATIC' | 'EMAIL_LINK';

export interface PledgeInstallment {
  id: string;
  sequence: number;
  amount_cents: number;
  due_at: string;
  status: string;
  paid_at: string | null;
  attempt_count: number;
  last_failure_message: string | null;
  reminder_sent_at: string | null;
  remind_at: string | null;
  donation_id: string | null;
}

export interface Pledge {
  id: string;
  campaign: { id: string; name: string; slug: string };
  status: string;
  overdue: boolean;
  total_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  installment_count: number;
  interval: PledgeInterval | null;
  collection_method: CollectionMethod;
  card_saved: boolean;
  card_consent_text: string | null;
  card_consented_at: string | null;
  donor: { name: string | null; email: string; customer_sub: string | null };
  wall_listing: string | null;
  in_memory_of: string | null;
  note: string | null;
  created_at: string;
  canceled_at: string | null;
  cancel_reason: string | null;
  fulfilled_at: string | null;
  next_due: { amount_cents: number; due_at: string } | null;
  installments?: PledgeInstallment[];
}

export interface PledgeTotals {
  pledged_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  active: number;
  overdue: number;
}

export interface NewPledge {
  campaign_id: string;
  total_cents: number;
  installment_count: number;
  interval: PledgeInterval | null;
  first_due_at: string;
  collection_method: CollectionMethod;
  donor_name: string | null;
  donor_email: string;
  wall_listing: 'NAME' | 'ANONYMOUS' | 'IN_MEMORY' | null;
  in_memory_of: string | null;
  note: string | null;
}

export interface PledgeAdjustment {
  total_cents?: number;
  remaining?: { installment_count: number; first_due_at: string; interval: PledgeInterval | null };
  collection_method?: CollectionMethod;
  reason: string;
}

export const planEndpoints = {
  packageTerms: (packageId: string) => api.get<{ installments: InstallmentTerms | null }>(`/api/admin/packages/${packageId}/installments`),
  setPackageTerms: (packageId: string, body: InstallmentTerms) => api.put<{ installments: InstallmentTerms }>(`/api/admin/packages/${packageId}/installments`, body),
  removePackageTerms: (packageId: string) => api.delete<{ installments: null }>(`/api/admin/packages/${packageId}/installments`),
  orderPlan: (orderId: string) => api.get<{ installment_plan: InstallmentPlan | null }>(`/api/admin/orders/${orderId}/installments`),
  stopOrderPlan: (orderId: string, reason: string) => api.post<{ installment_plan: InstallmentPlan | null }>(`/api/admin/orders/${orderId}/installments/stop`, { reason }),
  pledges: (campaignId: string | null, status: string | null) => {
    const query = new URLSearchParams();
    if (campaignId) query.set('campaign_id', campaignId);
    if (status) query.set('status', status);
    const qs = query.toString();
    return api.get<{ pledges: Pledge[]; totals: PledgeTotals }>(`/api/admin/pledges${qs ? `?${qs}` : ''}`);
  },
  pledge: (id: string) => api.get<{ pledge: Pledge }>(`/api/admin/pledges/${id}`),
  createPledge: (body: NewPledge) => api.post<{ pledge: Pledge; url: string }>('/api/admin/pledges', body),
  adjustPledge: (id: string, body: PledgeAdjustment) => api.patch<{ pledge: Pledge }>(`/api/admin/pledges/${id}`, body),
  cancelPledge: (id: string, reason: string) => api.post<{ pledge: Pledge }>(`/api/admin/pledges/${id}/cancel`, { reason }),
  pledgeLink: (id: string) => api.post<{ url: string }>(`/api/admin/pledges/${id}/link`),
};
